import { nextTick, onUnmounted, watch, type Ref } from "vue";

interface UseDialogA11yOptions {
  isOpen: Ref<boolean>;
  containerRef: Ref<HTMLElement | null>;
  onClose: () => void;
  focusInitial?: () => void;
}

const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(", ");

export function useDialogA11y({
  isOpen,
  containerRef,
  onClose,
  focusInitial,
}: UseDialogA11yOptions) {
  let lastFocusedElement: HTMLElement | null = null;

  function getFocusableElements(): HTMLElement[] {
    const container = containerRef.value;
    if (!container) return [];

    return Array.from(
      container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
    ).filter(
      (element) =>
        !element.hasAttribute("disabled") &&
        element.getAttribute("aria-hidden") !== "true" &&
        element.tabIndex !== -1 &&
        element.getClientRects().length > 0,
    );
  }

  function focusDialog() {
    if (focusInitial) {
      focusInitial();
      return;
    }

    const [firstFocusable] = getFocusableElements();
    (firstFocusable ?? containerRef.value)?.focus();
  }

  function handleKeydown(event: KeyboardEvent) {
    if (!isOpen.value) return;

    if (event.key === "Escape") {
      event.preventDefault();
      onClose();
      return;
    }

    if (event.key !== "Tab") return;

    const container = containerRef.value;
    if (!container) return;

    const focusableElements = getFocusableElements();
    if (focusableElements.length === 0) {
      event.preventDefault();
      container.focus();
      return;
    }

    const firstFocusable = focusableElements[0];
    const lastFocusable = focusableElements[focusableElements.length - 1];
    const activeElement = document.activeElement as HTMLElement | null;
    const isFocusInside = activeElement ? container.contains(activeElement) : false;

    if (event.shiftKey) {
      if (!isFocusInside || activeElement === firstFocusable) {
        event.preventDefault();
        lastFocusable.focus();
      }
      return;
    }

    if (!isFocusInside || activeElement === lastFocusable) {
      event.preventDefault();
      firstFocusable.focus();
    }
  }

  watch(
    isOpen,
    async (open) => {
      if (typeof document === "undefined") return;

      if (open) {
        lastFocusedElement = document.activeElement as HTMLElement | null;
        await nextTick();
        focusDialog();
        document.addEventListener("keydown", handleKeydown);
        return;
      }

      document.removeEventListener("keydown", handleKeydown);
      await nextTick();
      lastFocusedElement?.focus?.();
      lastFocusedElement = null;
    },
    { flush: "post" },
  );

  onUnmounted(() => {
    if (typeof document === "undefined") return;
    document.removeEventListener("keydown", handleKeydown);
  });
}
